import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle, Shield } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface WarrantyInfo {
  serialNumber: string;
  purchaseDate: string;
  initialCounter: number;
  warrantyStatus: 'en_garantie' | 'hors_garantie';
  warrantyEndDate: string;
  warrantyEndCounter: number;
  equipmentType: 'A4' | 'A3';
  warrantyEndReason: 'date' | 'counter';
}

const WarrantyVerification = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    serialNumber: '',
    currentCounter: '',
    captcha: ''
  });
  const [captchaQuestion, setCaptchaQuestion] = useState(() => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    return { a, b, answer: a + b };
  });
  const [warrantyInfo, setWarrantyInfo] = useState<WarrantyInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Demo data for different serial numbers
  const demoData: Record<string, WarrantyInfo> = {
    'ABC1234567890': {
      serialNumber: 'ABC1234567890',
      purchaseDate: '2023-06-15',
      initialCounter: 0,
      warrantyStatus: 'en_garantie',
      warrantyEndDate: '2023-09-15',
      warrantyEndCounter: 5000,
      equipmentType: 'A4',
      warrantyEndReason: 'date'
    },
    'XYZ9876543210': {
      serialNumber: 'XYZ9876543210',
      purchaseDate: '2021-03-10',
      initialCounter: 150,
      warrantyStatus: 'hors_garantie',
      warrantyEndDate: '2021-06-10',
      warrantyEndCounter: 5150,
      equipmentType: 'A4',
      warrantyEndReason: 'counter'
    },
    'DEF5678901234': {
      serialNumber: 'DEF5678901234',
      purchaseDate: '2024-01-20',
      initialCounter: 0,
      warrantyStatus: 'en_garantie',
      warrantyEndDate: '2024-07-20',
      warrantyEndCounter: 10000,
      equipmentType: 'A3',
      warrantyEndReason: 'date'
    }
  };

  const validateSerialNumber = (serial: string) => {
    const alphanumericRegex = /^[A-Z0-9]{13}$/;
    return alphanumericRegex.test(serial.toUpperCase());
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.serialNumber.trim()) {
      newErrors.serialNumber = 'Le numéro de série est requis';
    } else if (!validateSerialNumber(formData.serialNumber)) {
      newErrors.serialNumber = 'Le numéro de série doit contenir exactement 13 caractères alphanumériques';
    }

    if (!formData.currentCounter.trim()) {
      newErrors.currentCounter = 'Le compteur actuel est requis';
    } else if (parseInt(formData.currentCounter) < 0) {
      newErrors.currentCounter = 'Le compteur doit être un nombre positif';
    }

    if (!formData.captcha.trim()) {
      newErrors.captcha = 'Veuillez résoudre le captcha';
    } else if (parseInt(formData.captcha) !== captchaQuestion.answer) {
      newErrors.captcha = 'La réponse au captcha est incorrecte';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    // Simulate API call
    setTimeout(() => {
      const serialNumber = formData.serialNumber.toUpperCase();
      const warranty = demoData[serialNumber];

      if (warranty) {
        setWarrantyInfo(warranty);
        toast({
          title: "Vérification réussie",
          description: "Les informations de garantie ont été trouvées."
        });
      } else {
        // Generate demo data for any other serial number
        const equipmentType: 'A4' | 'A3' = serialNumber.startsWith('A4') || Math.random() > 0.5 ? 'A4' : 'A3';
        const warrantyMonths = equipmentType === 'A4' ? 3 : 6;
        const warrantyPages = equipmentType === 'A4' ? 5000 : 10000;
        
        const purchaseDate = new Date();
        purchaseDate.setFullYear(purchaseDate.getFullYear() - (Math.random() > 0.5 ? 0 : 1));
        
        const warrantyEndByDate = new Date(purchaseDate);
        warrantyEndByDate.setMonth(warrantyEndByDate.getMonth() + warrantyMonths);
        
        const initialCounter = Math.floor(Math.random() * 500);
        const warrantyEndCounter = initialCounter + warrantyPages;
        const currentCounter = parseInt(formData.currentCounter);
        
        // Determine if warranty has expired by date or counter
        const now = new Date();
        const expiredByDate = now > warrantyEndByDate;
        const expiredByCounter = currentCounter >= warrantyEndCounter;
        const isExpired = expiredByDate || expiredByCounter;
        
        const warrantyEndReason: 'date' | 'counter' = expiredByDate && !expiredByCounter ? 'date' : 'counter';

        const generatedWarranty: WarrantyInfo = {
          serialNumber: serialNumber,
          purchaseDate: purchaseDate.toISOString().split('T')[0],
          initialCounter: initialCounter,
          warrantyStatus: isExpired ? 'hors_garantie' : 'en_garantie',
          warrantyEndDate: warrantyEndByDate.toISOString().split('T')[0],
          warrantyEndCounter: warrantyEndCounter,
          equipmentType: equipmentType,
          warrantyEndReason: warrantyEndReason
        };

        setWarrantyInfo(generatedWarranty);
        toast({
          title: "Vérification réussie",
          description: "Les informations de garantie ont été trouvées."
        });
      }

      setIsLoading(false);
    }, 2000);
  };

  const refreshCaptcha = () => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    setCaptchaQuestion({ a, b, answer: a + b });
    setFormData(prev => ({ ...prev, captcha: '' }));
    setErrors(prev => ({ ...prev, captcha: '' }));
  };

  const resetForm = () => {
    setFormData({ serialNumber: '', currentCounter: '', captcha: '' });
    setWarrantyInfo(null);
    setErrors({});
    refreshCaptcha();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="container mx-auto px-4 py-8 print:px-0 print:py-4">
      <style>
        {`
          @media print {
            header, footer, .no-print {
              display: none !important;
            }
            body {
              print-color-adjust: exact;
            }
          }
        `}
      </style>
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">Vérification de Garantie</h1>
          <p className="text-muted-foreground">
            Vérifiez le statut de garantie de votre équipement en saisissant son numéro de série
          </p>
        </div>

        {!warrantyInfo ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Informations requises
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Serial Number */}
                <div className="space-y-2">
                  <Label htmlFor="serialNumber">
                    Numéro de série *
                    <span className="text-sm text-muted-foreground ml-2">
                      (13 caractères alphanumériques)
                    </span>
                  </Label>
                  <Input
                    id="serialNumber"
                    value={formData.serialNumber}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                      if (value.length <= 13) {
                        setFormData(prev => ({ ...prev, serialNumber: value }));
                        setErrors(prev => ({ ...prev, serialNumber: '' }));
                      }
                    }}
                    placeholder="Ex: ABC1234567890"
                    maxLength={13}
                    className={errors.serialNumber ? 'border-destructive' : ''}
                  />
                  {errors.serialNumber && (
                    <p className="text-sm text-destructive">{errors.serialNumber}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Le numéro de série se trouve généralement sur une étiquette à l'arrière ou sous l'appareil
                  </p>
                </div>

                {/* Current Counter */}
                <div className="space-y-2">
                  <Label htmlFor="currentCounter">Compteur actuel *</Label>
                  <Input
                    id="currentCounter"
                    type="number"
                    min="0"
                    value={formData.currentCounter}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, currentCounter: e.target.value }));
                      setErrors(prev => ({ ...prev, currentCounter: '' }));
                    }}
                    placeholder="Ex: 15420"
                    className={errors.currentCounter ? 'border-destructive' : ''}
                  />
                  {errors.currentCounter && (
                    <p className="text-sm text-destructive">{errors.currentCounter}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Le compteur actuel indique le nombre total de pages imprimées
                  </p>
                </div>

                {/* Captcha */}
                <div className="space-y-2">
                  <Label htmlFor="captcha">Vérification (Captcha) *</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 text-lg font-semibold">
                      <span>{captchaQuestion.a}</span>
                      <span>+</span>
                      <span>{captchaQuestion.b}</span>
                      <span>=</span>
                    </div>
                    <Input
                      id="captcha"
                      type="number"
                      value={formData.captcha}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, captcha: e.target.value }));
                        setErrors(prev => ({ ...prev, captcha: '' }));
                      }}
                      placeholder="?"
                      className={`w-20 ${errors.captcha ? 'border-destructive' : ''}`}
                    />
                    <Button type="button" variant="outline" size="sm" onClick={refreshCaptcha}>
                      Actualiser
                    </Button>
                  </div>
                  {errors.captcha && (
                    <p className="text-sm text-destructive">{errors.captcha}</p>
                  )}
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Vérification en cours...' : 'Vérifier la garantie'}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Résultats de la vérification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Warranty Status */}
              <div className="text-center p-6 rounded-lg border">
                <div className="flex items-center justify-center gap-2 mb-2">
                  {warrantyInfo.warrantyStatus === 'en_garantie' ? (
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  ) : (
                    <AlertCircle className="h-8 w-8 text-red-600" />
                  )}
                  <Badge 
                    variant={warrantyInfo.warrantyStatus === 'en_garantie' ? 'default' : 'destructive'}
                    className={`text-lg px-4 py-2 ${
                      warrantyInfo.warrantyStatus === 'en_garantie' 
                        ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                        : 'bg-red-100 text-red-800 hover:bg-red-200'
                    }`}
                  >
                    {warrantyInfo.warrantyStatus === 'en_garantie' ? 'En Garantie' : 'Hors Garantie'}
                  </Badge>
                </div>
                <p className="text-muted-foreground">
                  {warrantyInfo.warrantyStatus === 'en_garantie' 
                    ? 'Votre équipement est couvert par la garantie'
                    : 'La période de garantie de votre équipement est expirée'
                  }
                </p>
              </div>

              {/* Warranty Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <h4 className="font-medium">Numéro de série</h4>
                  <p className="font-mono text-sm bg-muted px-2 py-1 rounded">
                    {warrantyInfo.serialNumber}
                  </p>
                </div>
                
                <div className="space-y-1">
                  <h4 className="font-medium">Type d'équipement</h4>
                  <p className="text-sm">
                    {warrantyInfo.equipmentType === 'A4' ? 'Imprimante A4' : 'Photocopieur A3'}
                  </p>
                </div>
                
                <div className="space-y-1">
                  <h4 className="font-medium">Date d'achat</h4>
                  <p className="text-sm">{formatDate(warrantyInfo.purchaseDate)}</p>
                </div>
                
                <div className="space-y-1">
                  <h4 className="font-medium">Compteur initial</h4>
                  <p className="text-sm">{warrantyInfo.initialCounter.toLocaleString()} pages</p>
                </div>
                
                <div className="space-y-1">
                  <h4 className="font-medium">Fin de garantie</h4>
                  <p className="text-sm">
                    {warrantyInfo.warrantyEndReason === 'date' 
                      ? formatDate(warrantyInfo.warrantyEndDate)
                      : `${warrantyInfo.warrantyEndCounter.toLocaleString()} pages`
                    }
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {warrantyInfo.equipmentType === 'A4' 
                      ? '(3 mois ou 5 000 pages)'
                      : '(6 mois ou 10 000 pages)'
                    }
                  </p>
                </div>
                
                <div className="space-y-1">
                  <h4 className="font-medium">Raison de fin</h4>
                  <p className="text-sm">
                    {warrantyInfo.warrantyEndReason === 'date' ? 'Échéance' : 'Compteur atteint'}
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={resetForm} variant="outline" className="flex-1">
                  Nouvelle vérification
                </Button>
                <Button onClick={() => window.print()} variant="secondary">
                  Imprimer
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
};

export default WarrantyVerification;